// cspell:ignore Sommel Sauvignon Blanton's whisperoni Campari vivino untappd winecom openbedb cocktaildb Vivino Untappd
// src/components/IntegratedEnhancementDemo.js
/**
 * Integrated Enhancement Demo Component
 *
 * Demonstrates the full SommelAI enhancement pipeline that combines
 * AI-generated insights with external API data aggregation
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { enhanceBeverageComprehensively } from "../services/masterEnhancementService.js";
import { getRateLimitStatus } from "../services/externalApiIntegration.js";
import "./IntegratedEnhancementDemo.css";

const IntegratedEnhancementDemo = () => {
  const [enhancementResult, setEnhancementResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBeverage, setSelectedBeverage] = useState(null);
  const [rateLimitStatus, setRateLimitStatus] = useState({});
  const [processingMetrics, setProcessingMetrics] = useState(null);

  // Sample beverages for testing - memoized to prevent recreations
  const sampleBeverages = useMemo(
    () => [
      {
        name: "2019 Caymus Cabernet Sauvignon",
        winery: "Caymus Vineyards",
        region: "Napa Valley",
        vintage: 2019,
        varietal: "Cabernet Sauvignon",
        price: 85,
        description: "Rich and bold Napa Valley Cabernet Sauvignon",
        category: "wine",
      },
      {
        name: "Pliny the Elder",
        brewery: "Russian River Brewing Company",
        style: "Double IPA",
        abv: 8.0,
        ibu: 100,
        description: "Legendary double IPA with intense hop character",
        category: "beer",
      },
      {
        name: "Blanton's Single Barrel Bourbon",
        distillery: "Buffalo Trace Distillery",
        type: "Bourbon Whiskey",
        age: "8-10 years",
        abv: 46.5,
        description: "Premium single barrel bourbon",
        category: "spirit",
      },
      {
        name: "Classic Negroni",
        category: "cocktail",
        base: "Gin",
        ingredients: ["Gin", "Campari", "Sweet Vermouth"],
        description: "Classic Italian cocktail",
      },
    ],
    []
  );

  // Update rate limit status
  useEffect(() => {
    const updateRateLimits = () => {
      const apis = [
        "vivino",
        "untappd",
        "winecom",
        "openbedb",
        "whiskybase",
        "cocktaildb",
      ];
      const status = {};
      apis.forEach((api) => {
        status[api] = getRateLimitStatus(api);
      });
      setRateLimitStatus(status);
    };

    updateRateLimits();
    const interval = setInterval(updateRateLimits, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const handleEnhancement = useCallback(async (beverage) => {
    setIsLoading(true);
    setSelectedBeverage(beverage);
    setEnhancementResult(null);
    setProcessingMetrics(null);

    try {
      const startTime = Date.now();
      console.log(`🚀 Starting comprehensive enhancement for ${beverage.name}`);

      const result = await enhanceBeverageComprehensively(
        beverage,
        beverage.category,
        {
          includeMarketAnalysis: true,
          includePairings: true,
          useExternalAPIs: true,
        }
      );

      const processingTime = Date.now() - startTime;

      setEnhancementResult(result);
      setProcessingMetrics({
        processingTime,
        totalSources: [
          ...(result.enhanced?.aiSources || []),
          ...(result.externalData?.sources || []),
        ].length,
        confidenceScore: result.masterEnhancement?.confidence?.overall || 0,
      });

      console.log(`✅ Enhancement completed in ${processingTime}ms`);
    } catch (error) {
      console.error("Enhancement failed:", error);
      setEnhancementResult({ error: error.message });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const renderRateLimitStatus = () => (
    <div className="rate-limit-panel">
      <h3>🌐 API Rate Limit Status</h3>
      <div className="rate-limit-grid">
        {Object.entries(rateLimitStatus).map(([api, status]) => (
          <div key={api} className="rate-limit-item">
            <div className="api-name">{api.toUpperCase()}</div>
            <div className="rate-info">
              <span className="remaining">
                {status.remaining}/{status.limit}
              </span>
              <div className="rate-bar">
                <div
                  className="rate-fill"
                  style={{
                    width: `${(status.used / status.limit) * 100}%`,
                    backgroundColor:
                      status.remaining < 10
                        ? "#ff4444"
                        : status.remaining < 50
                        ? "#ffaa00"
                        : "#44ff44",
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderProcessingMetrics = () => {
    if (!processingMetrics) return null;

    return (
      <div className="processing-metrics">
        <h3>📊 Processing Metrics</h3>
        <div className="metrics-grid">
          <div className="metric">
            <span className="metric-label">Processing Time</span>
            <span className="metric-value">
              {processingMetrics.processingTime}ms
            </span>
          </div>
          <div className="metric">
            <span className="metric-label">Data Sources</span>
            <span className="metric-value">
              {processingMetrics.totalSources}
            </span>
          </div>
          <div className="metric">
            <span className="metric-label">Confidence Score</span>
            <span className="metric-value">
              {(processingMetrics.confidenceScore * 100).toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderEnhancementSummary = () => {
    if (!enhancementResult || enhancementResult.error) return null;

    const result = enhancementResult;
    const confidence = result.masterEnhancement?.confidence || {};

    return (
      <div className="enhancement-summary">
        <h3>🎯 Enhancement Summary</h3>

        <div className="confidence-breakdown">
          <h4>Confidence Breakdown</h4>
          <div className="confidence-bars">
            <div className="confidence-item">
              <span>AI Analysis</span>
              <div className="confidence-bar">
                <div
                  className="confidence-fill ai"
                  style={{ width: `${(confidence.ai || 0) * 100}%` }}
                />
              </div>
              <span>{((confidence.ai || 0) * 100).toFixed(1)}%</span>
            </div>
            <div className="confidence-item">
              <span>External Data</span>
              <div className="confidence-bar">
                <div
                  className="confidence-fill external"
                  style={{ width: `${(confidence.external || 0) * 100}%` }}
                />
              </div>
              <span>{((confidence.external || 0) * 100).toFixed(1)}%</span>
            </div>
            <div className="confidence-item">
              <span>Synthesis</span>
              <div className="confidence-bar">
                <div
                  className="confidence-fill synthesis"
                  style={{ width: `${(confidence.synthesis || 0) * 100}%` }}
                />
              </div>
              <span>{((confidence.synthesis || 0) * 100).toFixed(1)}%</span>
            </div>
            <div className="confidence-item overall">
              <span>Overall</span>
              <div className="confidence-bar">
                <div
                  className="confidence-fill overall"
                  style={{ width: `${(confidence.overall || 0) * 100}%` }}
                />
              </div>
              <span>{((confidence.overall || 0) * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>

        <div className="data-sources">
          <h4>Data Sources</h4>
          <div className="sources-grid">
            <div className="source-group">
              <h5>🤖 AI Sources</h5>
              <ul>
                {(result.enhanced?.aiSources || []).map((source, index) => (
                  <li key={index}>{source}</li>
                ))}
              </ul>
            </div>
            <div className="source-group">
              <h5>🌐 External Sources</h5>
              <ul>
                {(result.externalData?.sources || []).map((source, index) => (
                  <li key={index}>{source}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {result.ratings && (
          <div className="ratings-summary">
            <h4>⭐ Ratings Summary</h4>
            <div className="ratings-grid">
              <div className="rating-item">
                <span>Average Score</span>
                <span className="rating-value">
                  {result.ratings.averageScore?.toFixed(1) || "N/A"}/5
                </span>
              </div>
              <div className="rating-item">
                <span>Consensus</span>
                <span className="rating-consensus">
                  {result.ratings.consensusRating || "No consensus"}
                </span>
              </div>
              <div className="rating-item">
                <span>Professional Ratings</span>
                <span className="rating-count">
                  {result.ratings.professional?.length || 0}
                </span>
              </div>
              <div className="rating-item">
                <span>User Ratings</span>
                <span className="rating-count">
                  {result.ratings.user?.length || 0}
                </span>
              </div>
            </div>
          </div>
        )}

        {result.synthesisInsights && (
          <div className="synthesis-insights">
            <h4>🧠 AI Synthesis Insights</h4>
            <div className="insights-content">
              {result.synthesisInsights.marketPosition && (
                <div className="insight-item">
                  <strong>Market Position:</strong>{" "}
                  {result.synthesisInsights.marketPosition.priceRange ||
                    "Unknown"}
                </div>
              )}
              {result.synthesisInsights.recommendation && (
                <div className="insight-item">
                  <strong>Recommendation:</strong>{" "}
                  {result.synthesisInsights.recommendation}
                </div>
              )}
              {result.synthesisInsights.consensusAnalysis && (
                <div className="insight-item">
                  <strong>Consensus Analysis:</strong>{" "}
                  {result.synthesisInsights.consensusAnalysis}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="integrated-enhancement-demo">
      <div className="demo-header">
        <h1>🚀 SommelAI Integrated Enhancement Demo</h1>
        <p>
          Experience the power of AI + External API integration for
          comprehensive beverage intelligence
        </p>
      </div>

      {renderRateLimitStatus()}

      <div className="beverage-selection">
        <h2>🍷 Select a Beverage to Enhance</h2>
        <div className="beverage-grid">
          {sampleBeverages.map((beverage, index) => (
            <div
              key={index}
              className={`beverage-card ${
                selectedBeverage?.name === beverage.name ? "selected" : ""
              }`}
              onClick={() => handleEnhancement(beverage)}
            >
              <div className="beverage-category">{beverage.category}</div>
              <h3>{beverage.name}</h3>
              <p>{beverage.description}</p>
              <div className="beverage-details">
                {beverage.winery && <span>🏭 {beverage.winery}</span>}
                {beverage.brewery && <span>🍺 {beverage.brewery}</span>}
                {beverage.distillery && <span>🥃 {beverage.distillery}</span>}
                {beverage.vintage && <span>📅 {beverage.vintage}</span>}
                {beverage.abv && <span>🌡️ {beverage.abv}% ABV</span>}
                {beverage.price && <span>💰 ${beverage.price}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="loading-panel">
          <div className="loading-spinner"></div>
          <h3>🤖 Processing Enhancement...</h3>
          <p>Combining AI analysis with external API data...</p>
          <div className="loading-steps">
            <div className="step">🧠 Running AI enhancement...</div>
            <div className="step">🌐 Fetching external data...</div>
            <div className="step">🔄 Synthesizing results...</div>
          </div>
        </div>
      )}

      {processingMetrics && renderProcessingMetrics()}

      {enhancementResult &&
        !enhancementResult.error &&
        renderEnhancementSummary()}

      {enhancementResult?.error && (
        <div className="error-panel">
          <h3>❌ Enhancement Failed</h3>
          <p>{enhancementResult.error}</p>
        </div>
      )}

      <div className="demo-info">
        <h3>💡 About This Demo</h3>
        <div className="info-grid">
          <div className="info-item">
            <strong>🤖 AI Enhancement</strong>
            <p>
              Uses Claude and Gemini AI models to generate professional tasting
              notes, ratings, and food pairings
            </p>
          </div>
          <div className="info-item">
            <strong>🌐 External APIs</strong>
            <p>
              Integrates with Vivino, Untappd, Wine.com, and other platforms for
              real market data
            </p>
          </div>
          <div className="info-item">
            <strong>🔄 Data Synthesis</strong>
            <p>
              Intelligently combines AI and external data for comprehensive
              beverage intelligence
            </p>
          </div>
          <div className="info-item">
            <strong>⚡ Rate Limiting</strong>
            <p>
              Smart rate limiting ensures API compliance and optimal performance
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(IntegratedEnhancementDemo);
